import { DndContext, PointerSensor, TouchSensor, rectIntersection, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis, restrictToWindowEdges, snapCenterToCursor } from "@dnd-kit/modifiers";
import { SortableContext, arrayMove } from "@dnd-kit/sortable";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { useCan } from "components";
import { SortableItem, SortableOverlay } from "modules/PendingChecklistsDashboard/components/ServiceChecklists/components/ServiceChecklistModal/components";
import { useReorderServicePendingChecklistMutation } from "modules/PendingChecklistsDashboard/components/ServiceChecklists/components/ServiceChecklistModal/components/SortableList/hooks";
import "modules/PendingChecklistsDashboard/components/ServiceChecklists/components/ServiceChecklistModal/components/SortableList/SortableList.scss";
import { checkIfAannameChecklistsAreAtTheTop } from "modules/PendingChecklistsDashboard/util";

import type { DragEndEvent } from "@dnd-kit/core";
import type { Checklist } from "models";
import type { SortableServicePendingChecklist } from "modules/PendingChecklistsDashboard/components/ServiceChecklists/components";
import type { Dispatch, SetStateAction } from "react";

type SortableListProps = {
  checklists?: Checklist[];
  sortableServicePendingChecklists: SortableServicePendingChecklist[];
  setSortableServicePendingChecklists: Dispatch<SetStateAction<SortableServicePendingChecklist[]>>;
};

export const SortableList = ({ sortableServicePendingChecklists, setSortableServicePendingChecklists, checklists }: SortableListProps) => {
  const { t } = useTranslation();
  const sensors = useSensors(useSensor(PointerSensor), useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 100 } }));

  const canReorderServicePendingChecklist = useCan("update", "check-in-service-pending-checklist");
  const { mutateAsync: reorderServicePendingChecklistMutation } = useReorderServicePendingChecklistMutation();

  const handleOnDragEnd = async ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    try {
      const newIndex = sortableServicePendingChecklists.findIndex(item => item.id === over.id);
      const oldIndex = sortableServicePendingChecklists.findIndex(item => item.id === active.id);
      const sortedServicePendingChecklists = arrayMove(sortableServicePendingChecklists, oldIndex, newIndex).map((i, idx) => ({ ...i, ordering: idx + 1 }));

      if (checklists && !checkIfAannameChecklistsAreAtTheTop(sortedServicePendingChecklists, checklists)) {
        toast.error(t("v8_invalid_order_aanname_checklists_must_be_at_the_top_of_the_list", "Invalid order: Aanname checklists must be at the top of the list"));
        return;
      }

      setSortableServicePendingChecklists(sortedServicePendingChecklists);
      await reorderServicePendingChecklistMutation(sortedServicePendingChecklists);
    } catch (error) {
      setSortableServicePendingChecklists(sortableServicePendingChecklists);
      console.error(error);
    }
  };

  return (
    <div className="ServicePendingChecklistsSortableList">
      <DndContext
        sensors={sensors}
        onDragEnd={handleOnDragEnd}
        collisionDetection={rectIntersection}
        modifiers={[restrictToVerticalAxis, restrictToWindowEdges, snapCenterToCursor]}
      >
        <>
          <SortableContext items={sortableServicePendingChecklists} disabled={!canReorderServicePendingChecklist}>
            <ul className="check-container">
              {sortableServicePendingChecklists.map(item => (
                <SortableItem key={`pending-checklist-${item.checklist_id}`} item={item} checklists={checklists} />
              ))}
            </ul>
          </SortableContext>
          <SortableOverlay />
        </>
      </DndContext>
    </div>
  );
};
